import { useEffect, useRef } from 'react';
import { useGameStore, startTickLoop, stopTickLoop } from './game/store';
import { render } from './render/Renderer';
import { paletteFor } from './render/palette';
import { InputManager } from './game/input';
import * as audio from './game/audio';
import { HUD } from './ui/HUD';
import { PauseOverlay } from './ui/PauseOverlay';
import { GameOverScreen } from './ui/GameOverScreen';
import { StartScreen } from './ui/StartScreen';
import type { World } from './game/engine/types';
import { GlowLevel } from './data/settings';

const WORLD_WIDTH = 720;
/** Portrait aspect ratio (height = width * ASPECT). 9:16 is the standard
 *  phone-portrait shape; we use it so the game feels right on desktop too. */
const ASPECT = 16 / 9;
/** Target playable width on desktop; mobile falls back to viewport width. */
const MAX_CSS_WIDTH = 540;

function GameCanvas(): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const touchRef = useRef<HTMLDivElement | null>(null);
  const worldRef = useRef<World>(useGameStore.getState().world);

  // Subscribe to store updates so the canvas's refs always reflect the
  // latest world + settings. This decouples the rAF render loop from
  // React's render cycle — we draw straight from refs at 60fps.
  useEffect(() => {
    const unsub = useGameStore.subscribe((state) => {
      worldRef.current = state.world;
    });
    return unsub;
  }, []);

  // Mount: input + resize + render loop + tick loop.
  useEffect(() => {
    const canvas = canvasRef.current!;
    const container = containerRef.current!;
    const ctx = canvas.getContext('2d')!;

    const input = new InputManager();
    const touchTarget = touchRef.current!;
    // Attach to the full-screen touch wrapper so left/right touches work on full viewport
    input.attach(touchTarget, { fullScreen: true });
    const off = input.subscribe((s) => {
      audio.unlockAudio();
      useGameStore.getState().setSteer(s.steer);
      if (s.pausePressed) useGameStore.getState().togglePause();
      if (s.resetPressed) useGameStore.getState().newGame();
      if (s.quitPressed) useGameStore.getState().quitGame();
      if (s.cheatPressed) useGameStore.getState().toggleCheatInvincible();
    });

    const fit = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const maxByViewport = Math.floor(vh / ASPECT);
      const cssWidth = Math.max(280, Math.min(MAX_CSS_WIDTH, vw - 32, maxByViewport));
      const cssHeight = Math.round(cssWidth * ASPECT);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      container.style.width = `${cssWidth}px`;
      container.style.height = `${cssHeight}px`;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();
    window.addEventListener('resize', fit);

    startTickLoop();

    let raf: number | null = null;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const world = worldRef.current;
      const rect = canvas.getBoundingClientRect();
      const cssWidth = rect.width;
      const cssHeight = rect.height;
      const scale = cssWidth / WORLD_WIDTH;
      // The head's screen-y is dynamic: it lerps toward the bottom of the
      // canvas when the snake is short, and rises as the snake grows
      // (fewer world units between head and the next obstacle). The
      // engine maintains `world.headFrac`; we just multiply by height.
      const headOffsetY = cssHeight * world.headFrac;
      const palette = paletteFor(world.biome, 'NEON', 'OFF', GlowLevel.MEDIUM);
      render(ctx, world, {
        width: cssWidth,
        height: cssHeight,
        scale,
        headOffsetY,
        theme: 'NEON',
        glow: GlowLevel.MEDIUM,
        colorBlind: 'OFF',
      }, palette);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      window.removeEventListener('resize', fit);
      stopTickLoop();
      if (touchTarget) input.detach(touchTarget);
      off();
    };
  }, []);

  return (
    <>
      {/* Full-screen touch-capture layer behind the canvas. Covers the entire
          viewport so touches on either side register for left/right steering.
          The canvas and canvas-container use pointer-events:none so these
          touches fall through; HUD buttons and overlays opt back in. */}
      <div className="touch-capture" ref={touchRef} />
      <div className="canvas-container" ref={containerRef}>
        <canvas ref={canvasRef} />
        <HUD />
        <PauseOverlay />
        <GameOverScreen />
      </div>
    </>
  );
}

export function App(): JSX.Element {
  const screen = useGameStore((s) => s.screen);
  return (
    <div className="app">
      {screen === 'start' ? <StartScreen /> : <GameCanvas />}
    </div>
  );
}
